import { InputAdornment, SxProps, TextFieldProps, Theme, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { FC } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { DefaultLabelSx, DefaultSx, StyledTextField } from './GenericTextField.styles';
import { usePickerLocale } from '../../i18n/usePickerLocale';

import { palette } from '../../theme';
type GenericTextFieldProps = TextFieldProps & {
  label: string;
  sx?: SxProps<Theme>;
  isDate?: boolean;
  /** Earliest selectable date. Only used when isDate is set. */
  minDate?: string | Dayjs;
  /**
   * Latest selectable date. Defaults to today, which is what birth dates and
   * arrival times want; pass null for fields where the future is valid, such as a
   * payment due date or an invoice issue date.
   */
  maxDate?: string | Dayjs | null;
  /** Checked and right - a green tick at the end, beside a green helperText from the caller. */
  success?: boolean;
  /** Permanently fixed: disabled, with a lock at the end instead of an empty-looking field. */
  locked?: boolean;
};

const toDayjs = (value?: string | Dayjs | null): Dayjs | undefined => {
  if (value === undefined || value === null) return undefined;
  return typeof value === 'string' ? dayjs(value) : value;
};

const GenericTextField: FC<GenericTextFieldProps> = ({ label, sx, isDate, minDate, maxDate, success, locked, ...rest }) => {
  const theme = useTheme();
  // called unconditionally: a hook cannot sit behind the isDate branch
  const pickerLocale = usePickerLocale();
  const mergedSx: SxProps<Theme> = { ...DefaultSx, ...(sx as object) };

  if (isDate) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={pickerLocale.adapterLocale} localeText={pickerLocale.localeText}>
        <DatePicker
          label={label}
          value={rest.value ? dayjs(rest.value as string) : null}
          onChange={(newValue: Dayjs | null) => {
            if (rest.onChange) {
              const syntheticEvent = {
                target: { value: newValue ? newValue.format('YYYY-MM-DD') : '' }
              } as React.ChangeEvent<HTMLInputElement>;
              rest.onChange(syntheticEvent);
            }
          }}
          disabled={rest.disabled}
          minDate={toDayjs(minDate)}
          maxDate={maxDate === undefined ? dayjs() : toDayjs(maxDate)}
          views={['month', 'year', 'day']}
          slotProps={{
            yearButton: {
              sx: {
                color: 'text.primary'
              }
            },
            monthButton: {
              sx: {
                color: 'text.primary'
              }
            },
            textField: {
              required: rest.required,
              fullWidth: true,
              variant: 'outlined',
              sx: mergedSx,
              InputLabelProps: { sx: DefaultLabelSx(theme) },
              InputProps: {
                sx: {
                  borderRadius: '10px',
                  '& fieldset': {
                    borderRadius: '10px'
                  },
                  backgroundColor: palette.surface.main
                }
              }
            }
          }}
        />
      </LocalizationProvider>
    );
  }

  if (locked) {
    return (
      <StyledTextField
        fullWidth
        label={label}
        variant="outlined"
        sx={mergedSx}
        {...rest}
        disabled
        slotProps={{
          ...rest.slotProps,
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <LockOutlinedIcon aria-hidden sx={{ fontSize: 18, color: 'text.placeholder' }} />
              </InputAdornment>
            ),
            ...((rest.slotProps?.input as object) ?? {})
          }
        }}
      />
    );
  }

  if (success && !rest.error) {
    return (
      <StyledTextField
        fullWidth
        label={label}
        variant="outlined"
        sx={mergedSx}
        {...rest}
        slotProps={{
          ...rest.slotProps,
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 20, color: 'success.main' }} />
              </InputAdornment>
            ),
            ...((rest.slotProps?.input as object) ?? {})
          },
          formHelperText: { sx: { color: 'success.main' }, ...((rest.slotProps?.formHelperText as object) ?? {}) }
        }}
      />
    );
  }

  return <StyledTextField fullWidth label={label} variant="outlined" sx={mergedSx} {...rest} />;
};
export default GenericTextField;
